import React, { useState } from 'react';
import { submitToGoogleForm } from '../../utils/reportIssueForm/submitGoogleForm';
import { GOOGLE_FORM_ENTRY_IDS } from '../../utils/config';

const ReportIssueForm = () => {
  // Initialize form data
  const [formData, setFormData] = useState<IReportIssueFormData>({
    name: '',
    email: '',
    expectedOutcome: '',
    actualOutcome: '',
    stepsToReproduce: '',
    priorityLevel: 'Low',
    probabilityMotivation: '',
    additionalInformation: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get form data for Google Forms submission
      const formEntries = {
        [GOOGLE_FORM_ENTRY_IDS.name]: formData.name,
        [GOOGLE_FORM_ENTRY_IDS.email]: formData.email,
        [GOOGLE_FORM_ENTRY_IDS.expectedOutcome]: formData.expectedOutcome,
        [GOOGLE_FORM_ENTRY_IDS.actualOutcome]: formData.actualOutcome,
        [GOOGLE_FORM_ENTRY_IDS.stepsToReproduce]: formData.stepsToReproduce,
        [GOOGLE_FORM_ENTRY_IDS.priorityLevel]: formData.priorityLevel,
        [GOOGLE_FORM_ENTRY_IDS.probabilityMotivation]: formData.probabilityMotivation,
        [GOOGLE_FORM_ENTRY_IDS.additionalInformation]: formData.additionalInformation,
      };

      const res = await submitToGoogleForm(formEntries);
      if (res) setIsSubmitted(true);

      // Reset form data
      setFormData({
        name: '',
        email: '',
        expectedOutcome: '',
        actualOutcome: '',
        stepsToReproduce: '',
        priorityLevel: 'Low',
        probabilityMotivation: '',
        additionalInformation: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="m-[60px] bg-core-black-contrast p-8 rounded-xl border border-core-black-contrast-2">
      <div className="mb-6 text-center">
        <h2 className="text-3xl font-semibold text-white mb-2">Submit Issue Report</h2>
        <span className="inline-block px-4 py-2 text-sm font-medium tracking-wider text-white bg-core-black-contrast-2 rounded-lg mb-2">
          Encountering a bug or issue? Let us know so we can fix it!
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-white/80 text-sm font-medium mb-2">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white bg-transparent
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023  focus:outline-none focus:shadow-none"
            placeholder="Name"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-white/80 text-sm font-medium mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white bg-transparent 
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023  focus:outline-none focus:shadow-none"
            placeholder="example@mail.com"
            required
          />
        </div>

        <div>
          <label htmlFor="expectedOutcome" className="block text-white/80 text-sm font-medium mb-2">
            Expected outcome
          </label>
          <textarea
            id="expectedOutcome"
            name="expectedOutcome"
            value={formData.expectedOutcome}
            onChange={handleChange}
            rows={6}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white 
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023  focus:outline-none focus:shadow-none"
            placeholder="What should have happened?"
            required
          />
        </div>

        <div>
          <label htmlFor="actualOutcome" className="block text-white/80 text-sm font-medium mb-2">
            Actual outcome
          </label>
          <textarea
            id="actualOutcome"
            name="actualOutcome"
            value={formData.actualOutcome}
            onChange={handleChange}
            rows={6}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white 
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023  focus:outline-none focus:shadow-none"
            placeholder="What actually happened?"
            required
          />
        </div>

        <div>
          <label
            htmlFor="stepsToReproduce"
            className="block text-white/80 text-sm font-medium mb-2"
          >
            Steps to reproduce
          </label>
          <textarea
            id="stepsToReproduce"
            name="stepsToReproduce"
            value={formData.stepsToReproduce}
            onChange={handleChange}
            rows={6}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white 
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023 focus:outline-none focus:shadow-none"
            placeholder="List the steps needed to recreate the problem."
            required
          />
        </div>

        <div>
          <label htmlFor="priorityLevel" className="block text-white/80 text-sm font-medium mb-2">
            Priority level
          </label>
          <select
            id="priorityLevel"
            name="priorityLevel"
            value={formData.priorityLevel}
            onChange={handleChange}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white"
            required
          >
            <option value="Low" className="bg-core-black-contrast-3 text-white">
              Low
            </option>
            <option value="Medium" className="bg-core-black-contrast-3 text-white">
              Medium
            </option>
            <option value="High" className="bg-core-black-contrast-3 text-white">
              High
            </option>
            <option value="Critical" className="bg-core-black-contrast-3 text-white">
              Critical
            </option>
          </select>
        </div>

        <div>
          <label
            htmlFor="probabilityMotivation"
            className="block text-white/80 text-sm font-medium mb-2"
          >
            Probability motivation <span className="text-xs"> (Optional)</span>
          </label>
          <textarea
            id="probabilityMotivation"
            name="probabilityMotivation"
            value={formData.probabilityMotivation}
            onChange={handleChange}
            rows={3}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white 
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023  focus:outline-none focus:shadow-none"
            placeholder="How often does this issue occur?"
          />
        </div>

        <div>
          <label
            htmlFor="additionalInformation"
            className="block text-white/80 text-sm font-medium mb-2"
          >
            Additional information <span className="text-xs"> (Optional)</span>
          </label>
          <textarea
            id="additionalInformation"
            name="additionalInformation"
            value={formData.additionalInformation}
            onChange={handleChange}
            rows={3}
            className="w-full p-2 rounded-md border border-core-black-contrast-2 text-white 
            focus:ring-1 focus:ring-minima-orange-2023 focus:border-minima-orange-2023  focus:outline-none focus:shadow-none"
            placeholder="Anything else we should know?"
          />
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex items-center justify-center w-full">
          <button
            type="submit"
            className="btn-primary text-black bg-minima-orange-2023 cursor-pointer hover:bg-minima-orange-light transition-colors duration-600 ease-in-out"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit Report'
            )}
          </button>
        </div>
      </form>

      {/* SUCCESS MESSAGE */}
      {isSubmitted && (
        <div className="text-center text-minima-orange-2023 font-medium my-6">
          Your issue report has been submitted successfully! Thank you for your feedback.
        </div>
      )}
    </div>
  );
};

export default ReportIssueForm;
